/* eslint-disable camelcase */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import queryKeys from '@pray/shared/queryKeys';
import api from '@pray/shared/utils/api';

import type { ChatbotResponse, SaveChatbotParams } from './types';

export async function saveChatbot({
  artistId,
  instructions,
  chatOnlyInstructions,
  conversationStarter1,
  conversationStarter2,
  conversationStarter3,
  personaName,
  personaCharacteristics,
  personaTone,
  personaRole,
  isAiDisclaimerEnabled,
  aiDisclaimerText,
}: SaveChatbotParams): Promise<ChatbotResponse> {
  if (!artistId) throw Error('Artist ID is required');

  const url = `/web/studio/artists/${artistId}/chatbot/save`;
  const body: Record<string, string | boolean> = {};

  if (instructions !== undefined) body.instructions = instructions;
  if (chatOnlyInstructions !== undefined) body.chat_only_instructions = chatOnlyInstructions;
  if (conversationStarter1 !== undefined) body.conversation_starter_1 = conversationStarter1;
  if (conversationStarter2 !== undefined) body.conversation_starter_2 = conversationStarter2;
  if (conversationStarter3 !== undefined) body.conversation_starter_3 = conversationStarter3;
  if (personaName !== undefined) body.persona_name = personaName;
  if (personaCharacteristics !== undefined) body.persona_characteristics = personaCharacteristics;
  if (personaTone !== undefined) body.persona_tone = personaTone;
  if (personaRole !== undefined) body.persona_role = personaRole;
  if (isAiDisclaimerEnabled !== undefined) body.is_ai_disclaimer_enabled = isAiDisclaimerEnabled;
  if (aiDisclaimerText !== undefined) body.ai_disclaimer_text = aiDisclaimerText;

  const response = await api.executePostRequest<ChatbotResponse>(url, body);

  return response.data;
}

export function useSaveChatbot() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: saveChatbot,
    onSuccess: (_, { artistId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.artistChatbot(artistId),
      });
    },
  });

  const { mutateAsync, ...rest } = mutation;

  return {
    saveChatbot: mutateAsync,
    ...rest,
  };
}
